import {
  Edit,
  TabbedForm,
  FormTab,
  TextInput,
  Button,
  useRecordContext,
  BooleanInput,
  TopToolbar,
  useDataProvider,
  useNotify,
  SelectInput,
  FormDataConsumer,
  useGetRecordId,
  useRefresh
} from "react-admin";
import { useNavigate } from "react-router-dom";
import {
  Grid,
  Typography,
  Box,
  IconButton,
  Card,
  CardContent,
  Chip,
  Button as MuiButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Alert,
  Divider
} from "@mui/material";
import { Download as DownloadIcon, Schema as SchemaIcon, Code as CodeIcon, ExpandMore as ExpandMoreIcon, History as HistoryIcon, CheckCircle as CheckCircleIcon, Publish as PublishIcon, Settings as SettingsIcon, Build as BuildIcon, ArrowDropDown as ArrowDropDownIcon } from "@mui/icons-material";
import { useMemo, useRef, useCallback, useState, useEffect } from "react";
import ArrayEditor from "../../components/array/array.editor";
import SchemaEdit from "../schemas/SchemaEdit";
import { useAppDataContext } from "../../framework/app/app.data.context";
import { LazyAccordionIterator } from "../../components/array/LazyAccordionIterator";

const RegenerateButton = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const context = useAppDataContext();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleRegenerate = async (skipCode: boolean) => {
    handleClose();
    
    if (!record?.id) {
      notify('No data model loaded', { type: 'error' });
      return;
    }

    try {
      const actionType = skipCode ? 'Config Only' : 'Config & Code';
      notify(`Triggering ${actionType} generation...`, { type: 'info' });
      
      // Get auth token from app context
      const token = await context?.appContext?.tokenProvider?.getIdToken();
      const apiUrl = context?.apiUrl || 'http://localhost:5002';
      
      // Add skipCode=false query param for full code generation
      const queryParam = skipCode ? '' : '?skipCode=false';
      
      const response = await fetch(`${apiUrl}/data-model/${record.id}/generate${queryParam}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Generation failed: ${response.statusText} - ${errorText}`);
      }

      const result = await response.json();
      
      let message = `✅ Generated ${result.entitiesGenerated} entities for ${result.dataModelKey}`;
      if (result.codeGenerated) {
        message += ' (entity files created - restart server to load)';
      }
      
      notify(message, { type: 'success' });
      
      console.log('Generation result:', result);
    } catch (error) {
      console.error('Failed to trigger regeneration:', error);
      notify(`❌ ${error.message}`, { type: 'error' });
    }
  };

  return (
    <>
      <Button
        onClick={handleButtonClick}
        startIcon={<SchemaIcon />}
        label="Regenerate"
        variant="contained"
        color="primary"
        sx={{ 
          '& .MuiButton-endIcon': { marginLeft: '4px' }
        }}
      >
        <ArrowDropDownIcon sx={{ marginLeft: '4px' }} />
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={() => handleRegenerate(true)}>
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText 
            primary="Config Only"
            secondary="Editor schemas (fast, no restart)"
          />
        </MenuItem>
        <MenuItem onClick={() => handleRegenerate(false)}>
          <ListItemIcon>
            <BuildIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText 
            primary="Config & Code"
            secondary="Entity files + schemas (restart required)"
          />
        </MenuItem>
      </Menu>
    </>
  );
};

const PublishButton = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const context = useAppDataContext();
  const refresh = useRefresh();

  const isPublished = record?.published || false;

  const handleClick = async () => {
    if (!record?.id) {
      notify('No data model loaded', { type: 'error' });
      return;
    }

    try {
      const action = isPublished ? 'Unpublishing' : 'Publishing';
      notify(`${action} data model...`, { type: 'info' });
      
      // Get auth token from app context
      const token = await context?.appContext?.tokenProvider?.getIdToken();
      const apiUrl = context?.apiUrl || 'http://localhost:5002';
      
      // Use direct fetch to avoid dataProvider routing issues
      const response = await fetch(`${apiUrl}/datamodel-dp/${record.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...record,
          published: !isPublished,
          publishedAt: !isPublished ? new Date().toISOString() : null,
          publishedBy: !isPublished ? 'admin' : null
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Publish failed: ${response.statusText} - ${errorText}`);
      }

      notify(
        isPublished ? 'Data model unpublished' : '✅ Data model published and available for sync!',
        { type: 'success' }
      );
      
      // Refresh the record instead of reloading the whole page
      refresh();
    } catch (error) {
      console.error('Failed to publish/unpublish:', error);
      notify(`❌ ${error.message}`, { type: 'error' });
    }
  };

  return (
    <Button
      onClick={handleClick}
      startIcon={<PublishIcon />}
      label={isPublished ? "Unpublish" : "Publish"}
      variant="outlined"
      color={isPublished ? "secondary" : "success"}
    />
  );
};

const ExportButton = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const context = useAppDataContext();
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportOptions, setExportOptions] = useState({
    format: 'inline',
    includeMetadata: true,
    includeExamples: true,
    includeComments: false
  });

  const handleExportClick = () => {
    setExportDialogOpen(true);
  };

  const handleConfirmExport = async () => {
    setExportDialogOpen(false);
    
    if (!record?.key) {
      notify('No data model key found', { type: 'error' });
      return;
    }

    try {
      notify('Exporting data model...', { type: 'info' });
      
      // Get auth token from app context
      const token = await context?.appContext?.tokenProvider?.getIdToken();
      const apiUrl = context?.apiUrl || 'http://localhost:5002';
      
      // Build query parameters
      const params = new URLSearchParams({
        includeMetadata: String(exportOptions.includeMetadata),
        prettify: 'true',
        includeExamples: String(exportOptions.includeExamples)
      });
      
      // Fetch the export
      const response = await fetch(
        `${apiUrl}/data-model/export/${record.key}?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Export failed: ${response.statusText} - ${errorText}`);
      }

      const exportData = await response.json();
      
      // Add documentation metadata field if requested (valid JSON)
      if (exportOptions.includeComments) {
        exportData._export = {
          exportedAt: new Date().toISOString(),
          exportedBy: 'Proto Admin Portal',
          version: '1.0',
          instructions: {
            cli: `npm run import:data-model -- --file=./${exportData.key}.json`,
            api: 'POST /api/data-model/import/upload',
            description: 'Import this data model using the CLI command or API endpoint above'
          },
          notes: [
            `Data Model: ${exportData.title}`,
            `Key: ${exportData.key}`,
            `Version: ${exportData.version}`,
            `Schemas: ${exportData.schemas?.length || 0}`,
            `Relationships: ${exportData.relationships?.length || 0}`,
            'This file is 100% valid JSON and compatible with all JSON parsers'
          ]
        };
      }
      
      const fileContent = JSON.stringify(exportData, null, 2);
      
      // Create and download the file
      const dataBlob = new Blob([fileContent], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = `${exportData.key || 'data-model'}-export.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      // Build success message
      const parts = [];
      if (exportOptions.includeMetadata) parts.push('metadata');
      if (exportOptions.includeExamples) parts.push('examples');
      if (exportOptions.includeComments) parts.push('documentation');
      
      const message = parts.length > 0
        ? `Data model exported with: ${parts.join(', ')} (valid JSON)`
        : 'Data model exported (valid JSON)';
      
      notify(message, { type: 'success' });
    } catch (error) {
      console.error('Export failed:', error);
      notify(`❌ Export failed: ${error.message}`, { type: 'error' });
    }
  };

  return (
    <>
      <Button
        onClick={handleExportClick}
        startIcon={<DownloadIcon />}
        label="Export"
        variant="outlined"
      />

      {/* Export Options Dialog */}
      <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Export Data Model Options</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose what to include in your data model export. The schemas and relationships will always be included.
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={exportOptions.includeMetadata}
                  onChange={(e) => setExportOptions({ ...exportOptions, includeMetadata: e.target.checked })}
                />
              }
              label={
                <Box>
                  <Typography variant="body2"><strong>Metadata</strong></Typography>
                  <Typography variant="caption" color="text.secondary">
                    Include export timestamp, version info, and other metadata (recommended)
                  </Typography>
                </Box>
              }
            />
            
            <FormControlLabel
              control={
                <Checkbox
                  checked={exportOptions.includeExamples}
                  onChange={(e) => setExportOptions({ ...exportOptions, includeExamples: e.target.checked })}
                />
              }
              label={
                <Box>
                  <Typography variant="body2"><strong>Schema Examples</strong></Typography>
                  <Typography variant="caption" color="text.secondary">
                    Include example data in schema definitions (helpful for understanding)
                  </Typography>
                </Box>
              }
            />
            
            <Divider sx={{ my: 2 }} />
            
            <FormControlLabel
              control={
                <Checkbox
                  checked={exportOptions.includeComments}
                  onChange={(e) => setExportOptions({ ...exportOptions, includeComments: e.target.checked })}
                />
              }
              label={
                <Box>
                  <Typography variant="body2"><strong>Documentation Field</strong></Typography>
                  <Typography variant="caption" color="text.secondary">
                    Add structured _export field with documentation and import instructions (valid JSON)
                  </Typography>
                </Box>
              }
            />
          </Box>
          
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="caption">
              <strong>Note:</strong> Exported data models can be imported into any environment using the CLI or API.
              Use this for backup, version control, or cross-environment deployment.
            </Typography>
          </Alert>
          
          <Alert severity="success" sx={{ mt: 1 }}>
            <Typography variant="caption">
              <strong>AI Training:</strong> Exported models can be used as examples for AI tools (ChatGPT, Claude) 
              to generate similar data models for your projects.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialogOpen(false)} label="Cancel" />
          <Button 
            onClick={handleConfirmExport} 
            label="Export" 
            variant="contained"
            color="primary"
          />
        </DialogActions>
      </Dialog>
    </>
  );
};

const DataModelEdit = () => {
  const navigate = useNavigate();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const record = useRecordContext();
  const recordId = useGetRecordId();
  
  // Store the original schemas to preserve them during save
  const schemasRef = useRef<any[]>([]);
  
  // Update ref whenever record changes
  if (record?.schemas && Array.isArray(record.schemas)) {
    schemasRef.current = record.schemas;
  }

  // Memoize schema field parsing to prevent re-parsing on every render
  const schemaFieldsCache = useMemo(() => {
    const cache = new Map<string, any[]>();
    const schemas = record?.schemas || [];
    
    schemas.forEach((schema: any) => {
      if (!schema?.jsonSchema) return;
      
      try {
        const parsed = JSON.parse(schema.jsonSchema);
        const properties = parsed.properties || {};
        const fields = Object.keys(properties).map(key => ({
          id: key,
          name: `${key} (${properties[key].type || 'any'})`
        }));
        cache.set(schema.key, fields);
      } catch (error) {
        cache.set(schema.key, [{ id: 'id', name: 'id (parse error)' }]);
      }
    });
    
    return cache;
  }, [record?.schemas]);

  // Memoize schema choices to prevent recalculating on every relationship render
  const schemaChoices = useMemo(() => {
    const schemas = record?.schemas || [];
    return schemas.map((schema: any, index: number) => ({
      id: schema.key || schema.name?.toLowerCase() || `schema-${index}`,
      name: `${schema.name} (${schema.key || schema.name?.toLowerCase()})`
    }));
  }, [record?.schemas]);

  // Schema attributes for ArrayEditor (following Application pattern)
  const schemaAttributes = [
    { source: "name", label: "Name" },
    { source: "key", label: "Key" },
    { source: "category", label: "Category" }
  ];

  // Function to create new schema (following Application pattern)
  const getNewSchema = () => ({
    name: '',
    key: '',
    description: '',
    domain: 'identity', // Default domain (could be derived from DataModel scope)
    category: 'entity', // Always entity for DataModel schemas
    configuration: {
      isCore: false,
      allowExtensions: true,
      requiresSync: false,
      tags: []
    },
    jsonSchema: JSON.stringify({
      "$schema": "http://json-schema.org/draft-07/schema#",
      "type": "object",
      "title": "New Schema",
      "description": "Schema definition",
      "properties": {
        "id": {
          "type": "string",
          "format": "uuid",
          "description": "Unique identifier"
        }
      },
      "required": ["id"],
      "additionalProperties": false
    }, null, 2)
  });

  // Transform function to preserve schemas array with full objects
  const transform = useCallback((data: any) => {
    console.log('🔄 TRANSFORM CALLED - DataModel save transform', {
      hasSchemas: !!data.schemas,
      schemasType: Array.isArray(data.schemas) ? 'array' : typeof data.schemas,
      schemasLength: data.schemas?.length,
      schemasContent: data.schemas?.slice(0, 2),  // First 2 items
      allKeys: Object.keys(data),
      fullData: data
    });
    
    // CRITICAL FIX: Always use schemasRef if data.schemas is empty or missing
    if (!data.schemas || !Array.isArray(data.schemas) || data.schemas.length === 0) {
      console.warn('  ⚠️ No schemas array in save data! Restoring from ref/record.');
      
      // Try to restore from ref first (most current) - preserve full objects
      if (schemasRef.current && schemasRef.current.length > 0) {
        console.log(`  🔧 Restoring schemas from ref (${schemasRef.current.length} schemas) - keeping full objects`);
        data.schemas = schemasRef.current;
      } 
      // Fallback to record
      else if (record?.schemas && Array.isArray(record.schemas) && record.schemas.length > 0) {
        console.log(`  🔧 Restoring schemas from record (${record.schemas.length} schemas) - keeping full objects`);
        data.schemas = record.schemas;
      }
      else {
        console.error('  ❌ No schemas available to restore! This will clear all schemas.');
      }
    } else {
      // Schema array exists - preserve full objects so server can detect changes
      console.log(`  ✅ Preserving ${data.schemas.length} schema objects (with any modifications)`);
      // Don't transform - keep the full objects with all their properties
      // The server middleware needs the full objects to detect changes by timestamp/lastModified
    }
    
    console.log('  📤 Final schemas to save:', data.schemas?.length, 'objects');
    console.log('  📤 Sample schema:', data.schemas?.[0]);
    return data;
  }, [record, schemasRef]);

  return (
    <Edit
      transform={transform}
      mutationMode="pessimistic"
      mutationOptions={{
        onSuccess: (data) => {
          console.log('✅ Save successful!', data);
          notify('Data model saved successfully', { type: 'success' });
        },
        onError: (error: any) => {
          console.error('❌ Save failed!', error);
          notify(`Failed to save: ${error?.message || String(error)}`, { type: 'error' });
        },
        onSettled: (data, error, variables) => {
          console.log('🏁 Save settled', { data, error, variables });
        }
      }}
      actions={
        <TopToolbar>
          <PublishButton />
          <RegenerateButton />
          <ExportButton />
        </TopToolbar>
      }
    >
      <TabbedForm>
        <FormTab label="Basic Info">
          <TextInput source="name" label="Name" required fullWidth />
          <TextInput source="key" label="Key" required fullWidth />
          <TextInput source="description" label="Description" multiline rows={3} fullWidth />
          
          <SelectInput
            source="scope"
            label="Scope"
            choices={[
              { id: 'solution-space', name: 'Solution Space' },
              { id: 'application', name: 'Application' },
              { id: 'domain', name: 'Domain' },
              { id: 'industry', name: 'Industry Extension' },
            ]}
            fullWidth
          />
          
          {/* Versioning and Publishing Status */}
          <FormDataConsumer>
            {({ formData }) => {
              const currentVersion = formData?.currentVersion || 1;
              const versionLabel = formData?.versionLabel || 'v1';
              const isPublished = formData?.published || false;
              const publishedAt = formData?.publishedAt 
                ? new Date(formData.publishedAt).toLocaleString() 
                : null;
              
              return (
                <Box sx={{ mt: 3, p: 2, backgroundColor: 'rgba(255, 255, 255, 0.02)', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Version & Publishing Status
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
                    <Chip 
                      label={`Current Version: v${currentVersion}`}
                      icon={<HistoryIcon />}
                      size="small"
                      color="primary"
                    />
                    <Chip 
                      label={isPublished ? `Published ${publishedAt ? `on ${publishedAt}` : ''}` : 'Not Published'}
                      icon={isPublished ? <CheckCircleIcon /> : undefined}
                      size="small"
                      color={isPublished ? 'success' : 'default'}
                    />
                  </Box>
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
                    {isPublished 
                      ? '✅ This data model is published and available for child environments to sync'
                      : '⚠️ Publish this data model to make it available for child environments'}
                  </Typography>
                </Box>
              );
            }}
          </FormDataConsumer>
        </FormTab>

        <FormTab label="Schema Composition">
          <Typography variant="h6" gutterBottom>
            Schema Management
          </Typography>
          <Typography variant="body2" color="textSecondary" paragraph>
            Manage the schemas that make up this data model. Each schema represents a core business entity.
          </Typography>
          
          {/* Debug: Monitor form data */}
          <FormDataConsumer>
            {({ formData }) => {
              console.log('📋 FormData schemas:', {
                hasSchemas: !!formData?.schemas,
                schemasType: typeof formData?.schemas,
                isArray: Array.isArray(formData?.schemas),
                length: formData?.schemas?.length,
                first3: formData?.schemas?.slice(0, 3)
              });
              return null;
            }}
          </FormDataConsumer>
          
          {/* Enhanced Schema Management using ArrayEditor (following Application pattern) */}
          <ArrayEditor
            cardLabel="Schema"
            source="schemas"
            attributes={schemaAttributes}
            getNewItem={getNewSchema}
            EditComponent={SchemaEdit}
          />
        </FormTab>

        <FormTab label="Relationships">
          <Typography variant="h6" gutterBottom>
            Schema Relationships
          </Typography>
          <Typography variant="body2" color="textSecondary" paragraph>
            Define how schemas in this data model relate to each other (e.g., Order → Customer, OrderItem → Product).
          </Typography>
          
          {/* Using the reusable LazyAccordionIterator component */}
          <LazyAccordionIterator
            source="configuration.relationships"
            label="Schema Relationships"
            getItemId={(item) => item?.id}
            renderSummary={(item, index, formData) => {
              const schemas = formData?.schemas || [];
              const getSchemaName = (key: string) => {
                const schema = schemas.find((s: any) => s.key === key);
                return schema?.name || key;
              };
              
              const relationshipName = item?.name || 'New Relationship';
              const sourceSchemaName = getSchemaName(item?.sourceSchemaKey);
              const targetSchemaName = getSchemaName(item?.targetSchemaKey);
              const relType = item?.relationshipType || 'many-to-one';
              const isRequired = item?.isRequired !== false;
              
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, width: '240px', flexShrink: 0 }}>
                    {relationshipName}
                  </Typography>
                  <Box sx={{ width: '220px', flexShrink: 0 }}>
                    <Chip 
                      label={`${sourceSchemaName} → ${targetSchemaName}`} 
                      size="small" 
                      color="primary" 
                      variant="outlined"
                      sx={{ maxWidth: '100%' }}
                    />
                  </Box>
                  <Box sx={{ width: '130px', flexShrink: 0 }}>
                    <Chip 
                      label={relType} 
                      size="small" 
                      color="info"
                      sx={{ width: '100%' }}
                    />
                  </Box>
                  <Box sx={{ width: '80px', flexShrink: 0 }}>
                    {isRequired && (
                      <Chip 
                        label="Required" 
                        size="small" 
                        color="error" 
                        variant="outlined"
                        sx={{ width: '100%' }}
                      />
                    )}
                  </Box>
                </Box>
              );
            }}
            renderDetails={(item, index, formData, getSource) => {
              const schemas = formData?.schemas || [];
              
              // Get fields from selected schemas (using memoized cache for performance)
              const getSchemaFields = (schemaKey: string) => {
                if (!schemaKey) return [{ id: 'id', name: 'id (default)' }];
                const cachedFields = schemaFieldsCache.get(schemaKey);
                return cachedFields || [{ id: 'id', name: 'id (not found)' }];
              };

              const sourceSchemaKey = item?.sourceSchemaKey;
              const targetSchemaKey = item?.targetSchemaKey;
              const sourceFieldChoices = getSchemaFields(sourceSchemaKey);
              const targetFieldChoices = getSchemaFields(targetSchemaKey);

              return (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextInput 
                      source={getSource('name')} 
                      label="Relationship Name" 
                      placeholder="e.g., Customer Orders" 
                      fullWidth 
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextInput 
                      source={getSource('description')} 
                      label="Description" 
                      placeholder="e.g., Each order belongs to a customer" 
                      multiline 
                      rows={2}
                      fullWidth 
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <SelectInput
                      source={getSource('sourceSchemaKey')}
                      label="Source Schema"
                      choices={schemaChoices}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <SelectInput
                      source={getSource('targetSchemaKey')}
                      label="Target Schema"
                      choices={schemaChoices}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <SelectInput
                      source={getSource('sourceField')}
                      label="Source Field"
                      choices={sourceFieldChoices}
                      key={`source-field-${sourceSchemaKey}`}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <SelectInput
                      source={getSource('targetField')}
                      label="Target Field"
                      choices={targetFieldChoices}
                      key={`target-field-${targetSchemaKey}`}
                      defaultValue="id"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <SelectInput
                      source={getSource('relationshipType')}
                      label="Relationship Type"
                      choices={[
                        { id: 'one-to-one', name: 'One to One' },
                        { id: 'one-to-many', name: 'One to Many' },
                        { id: 'many-to-one', name: 'Many to One' },
                        { id: 'many-to-many', name: 'Many to Many' },
                      ]}
                      defaultValue="many-to-one"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <BooleanInput 
                      source={getSource('isRequired')} 
                      label="Required Relationship" 
                    />
                  </Grid>
                </Grid>
              );
            }}
          />
        </FormTab>

        <FormTab label="Preview">
          <DataModelPreview />
        </FormTab>

        <FormTab label="API">
          <DataModelApiDocs />
        </FormTab>

        <FormTab label="Version History">
          <DataModelVersionHistory />
        </FormTab>
      </TabbedForm>
    </Edit>
  );
};

// DataModel API Documentation component (iframe Swagger)
const DataModelApiDocs: React.FC = () => {
  const record = useRecordContext();
  const context = useAppDataContext();
  const notify = useNotify();
  const apiUrl = context?.apiUrl || 'http://localhost:5002';
  
  // Use specific data model docs if key is available, otherwise fall back to combined view
  const dataModelKey = record?.key;
  const apiDocsUrl = dataModelKey 
    ? `${apiUrl}/data-models/${dataModelKey}/docs`
    : `${apiUrl}/data`;
  
  const handleCopyToken = async () => {
    try {
      const token = await context?.appContext?.tokenProvider?.getIdToken();
      if (token) {
        await navigator.clipboard.writeText(token);
        notify('✅ Bearer token copied to clipboard! Paste it in the Swagger "Authorize" dialog.', { type: 'success' });
      } else {
        notify('❌ No auth token available', { type: 'error' });
      }
    } catch (error) {
      console.error('Failed to copy token:', error);
      notify('❌ Failed to copy token', { type: 'error' });
    }
  };
  
  return (
    <Box sx={{ width: '100%', height: '900px' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h6">
            {dataModelKey ? `${record?.name || dataModelKey} API Documentation` : 'Generated Data Entity API Documentation'}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {dataModelKey 
              ? `REST API endpoints for ${record?.name || dataModelKey} data entities. Click "Authorize" in Swagger and paste your bearer token.`
              : 'REST API endpoints for all data entities. Click "Authorize" in Swagger and paste your bearer token.'
            }
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {dataModelKey && (
            <MuiButton
              onClick={() => window.open(`${apiUrl}/data`, '_blank')}
              startIcon={<SchemaIcon />}
              variant="outlined"
              size="small"
              sx={{ textTransform: 'none' }}
            >
              View All Data Models
            </MuiButton>
          )}
          <MuiButton
            onClick={handleCopyToken}
            startIcon={<CodeIcon />}
            variant="contained"
            color="success"
            size="small"
            sx={{ textTransform: 'none' }}
          >
            Copy Bearer Token
          </MuiButton>
        </Box>
      </Box>
      {!dataModelKey && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No data model key available. Showing combined API documentation for all data models.
        </Alert>
      )}
      <iframe
        src={apiDocsUrl}
        width="100%"
        height="850px"
        style={{ border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '4px' }}
        title={dataModelKey ? `${dataModelKey} API Documentation` : 'Data Model API Documentation'}
      />
    </Box>
  );
};

// DataModel preview component
const DataModelPreview: React.FC = () => {
  const record = useRecordContext();
  
  if (!record) {
    return <Typography>No data model loaded</Typography>;
  }

  const schemaCount = record.schemas?.length || 0;
  const versionInfo = record.configuration?.version || record.currentVersion || '1.0.0';

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Data Model Overview
      </Typography>
      
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6}>
          <Card>
            <CardContent>
              <Typography variant="h4">{schemaCount}</Typography>
              <Typography variant="body2" color="textSecondary">
                Schemas Defined
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6}>
          <Card>
            <CardContent>
              <Typography variant="h4">{versionInfo}</Typography>
              <Typography variant="body2" color="textSecondary">
                Current Version
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Schema summary */}
      {record.schemas && record.schemas.length > 0 && (
        <Box>
          <Typography variant="subtitle1" gutterBottom>
            Schemas in this Data Model:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {record.schemas.map((schema: any, index: number) => (
              <Chip 
                key={index}
                label={`${schema.name || 'Unnamed'} (${schema.domain || 'No Domain'})`}
                variant="outlined"
                size="small"
              />
            ))}
          </Box>
        </Box>
      )}

      {/* Compiled master schema preview */}
      {record.compiledMasterSchema && (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1">
              Compiled Master Schema
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <pre style={{
              backgroundColor: '#f5f5f5',
              padding: '10px',
              borderRadius: '4px',
              overflow: 'auto',
              maxHeight: '400px',
              fontSize: '12px'
            }}>
              {JSON.stringify(JSON.parse(record.compiledMasterSchema), null, 2)}
            </pre>
          </AccordionDetails>
        </Accordion>
      )}
    </Box>
  );
};

// Version History component
const DataModelVersionHistory: React.FC = () => {
  const record = useRecordContext();
  
  if (!record) {
    return <Typography>No data model loaded</Typography>;
  }

  const versions = record.versions || [];
  const currentVersion = record.currentVersion || 1;

  if (versions.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="body1" color="textSecondary">
          No version history available yet. Versions will be created automatically when you make changes to the data model.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Version History
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HistoryIcon color="action" />
          <Typography variant="body2" color="textSecondary">
            {versions.length} version{versions.length !== 1 ? 's' : ''} tracked
          </Typography>
          <Chip 
            label={`Current: v${currentVersion}`}
            size="small"
            color="primary"
            sx={{ ml: 1 }}
          />
        </Box>
      </Box>

      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>Version</strong></TableCell>
              <TableCell><strong>Description</strong></TableCell>
              <TableCell><strong>Created</strong></TableCell>
              <TableCell><strong>Created By</strong></TableCell>
              <TableCell align="center"><strong>Published</strong></TableCell>
              <TableCell align="center"><strong>Current</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Reverse to show newest first */}
            {[...versions].reverse().map((version: any, index: number) => {
              const isCurrent = version.versionNumber === currentVersion;
              const createdDate = version.createdAt 
                ? new Date(version.createdAt).toLocaleString() 
                : 'Unknown';
              
              return (
                <TableRow 
                  key={version.id || index}
                  sx={{ 
                    backgroundColor: isCurrent ? 'rgba(25, 118, 210, 0.08)' : 'inherit',
                    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' }
                  }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: isCurrent ? 600 : 400 }}>
                        v{version.versionNumber}
                      </Typography>
                      {isCurrent && (
                        <Chip label="ACTIVE" size="small" color="primary" sx={{ height: 20 }} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {version.description || 'No description'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="textSecondary">
                      {createdDate}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {version.createdBy || 'Unknown'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    {version.isPublished ? (
                      <CheckCircleIcon color="success" fontSize="small" />
                    ) : (
                      <Typography variant="body2" color="textSecondary">—</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {isCurrent ? (
                      <CheckCircleIcon color="primary" fontSize="small" />
                    ) : (
                      <Typography variant="body2" color="textSecondary">—</Typography>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>

      {/* Summary footer */}
      <Box sx={{ mt: 2, p: 2, backgroundColor: 'rgba(255, 255, 255, 0.02)', borderRadius: 1 }}>
        <Typography variant="body2" color="textSecondary">
          💡 <strong>Tip:</strong> New versions are created automatically when you make meaningful changes 
          to the data model configuration or schemas. The current version (v{currentVersion}) is the active one.
        </Typography>
      </Box>
    </Box>
  );
};

export default DataModelEdit;
